@use "@styles/_variables/colors" as *;
@use "@styles/_variables/breakpoints" as *;
@use "@styles/_variables/sizes" as *;
@use "@styles/_variables/mixins" as *;
@use "@styles/_variables/typography" as *;

.contact {
  position: relative;
}

// Comms deck: the holo-framed beacon image and the channel links share one
// row, aligned to the same side margins as the section card above.
.contact-row {
  display: flex;
  align-items: center;
  gap: 2.6rem;
  margin: $tab-double ($header-logo-size + $header-logo-spacing)
    $tab-quadruple;

  .viewport {
    flex: 0 0 52%;
    min-width: 0;
  }

  .panel {
    flex: 1;
    min-width: 0;
  }

  // Data-plate header: a clean hairline underline. The section card above
  // already carries the gold tick cluster; repeating it here would stack
  // the same ornament four deep in one column.
  //
  // The plate tops the whole comms deck — a peer of the section card's h2,
  // not a heading nested inside a card body — so it keeps the full
  // section-title scale instead of stepping down to the card sub-title one.
  h4 {
    position: relative;
    padding-bottom: 0.9rem;
    border-bottom: 0.1rem solid $main-medium-transp;
    font-family: $font-display-bold;
    font-size: $section-title-font-size;
    color: $white;
    @include sciFiTitleGlow();
  }

  .channels {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 1.4rem;
    margin: 2.2rem 0 0;
    padding: 0;
  }

  // Channel chip: chamfered glass plate. A real border can't follow the
  // chamfer clip — the clip cuts the stroke off at every corner, leaving
  // the plate open exactly where the shape happens. So the outline is the
  // frame vocabulary's clip-path RING on ::before (outer loop CW, inner
  // loop CCW, nonzero winding keeps the band): steel at rest, hot gold on
  // approach, diagonals stroked and all.
  //
  // Geometry rides the shared registered --tc-* properties (@property
  // <length> in styles.scss — registrations are global and serve every
  // consuming module), so the corners TWEEN on hover like the cards'
  // frames. Values are chip-scaled: the cards' 2.8rem deep cut would eat
  // half this plate.
  .channel {
    --tc-cut-tl: 0.9rem;
    --tc-cut-tr: 0.9rem;
    --tc-cut-br: 0.9rem;
    --tc-cut-bl: 0.9rem;
    --tc-line: 0.15rem;

    position: relative;
    display: flex;
    align-items: center;
    gap: 1.8rem;
    padding: 1.2rem 1.8rem;
    // Plate label, not running copy: these rows are tap targets, so they
    // keep the identity voice. Size stays token-driven off the retuned
    // section-text scale.
    font-family: $font-display;
    font-size: $section-text-font-size;
    color: $main-lightest;
    text-decoration: none;
    background: #00000044;
    backdrop-filter: blur(4px);
    clip-path: polygon(
      var(--tc-cut-tl) 0,
      calc(100% - var(--tc-cut-tr)) 0,
      100% var(--tc-cut-tr),
      100% calc(100% - var(--tc-cut-br)),
      calc(100% - var(--tc-cut-br)) 100%,
      var(--tc-cut-bl) 100%,
      0 calc(100% - var(--tc-cut-bl)),
      0 var(--tc-cut-tl)
    );
    transition:
      --tc-cut-tl 0.35s ease,
      --tc-cut-tr 0.35s ease,
      --tc-cut-br 0.35s ease,
      --tc-cut-bl 0.35s ease,
      --tc-line 0.35s ease,
      color 0.2s ease,
      background 0.2s ease,
      text-shadow 0.2s ease;
    @include hideTapHighlightColor();

    // The outline band. An outer box-shadow would be clipped away with the
    // corners, so hover heat lives in the ring color and the inner fill.
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background-color: $main-darkish;
      transition: background-color 0.2s ease;
      // Inner-offset helpers live ON the pseudo so they re-resolve from
      // the animating inherited cut/line values every frame; max() clamps
      // a cut tweening to 0 into a clean square corner (0.414 = sqrt(2)-1
      // keeps the inner diagonal parallel at stroke distance).
      --i-tl: max(0px, calc(var(--tc-cut-tl) - 0.414 * var(--tc-line)));
      --i-tr: max(0px, calc(var(--tc-cut-tr) - 0.414 * var(--tc-line)));
      --i-br: max(0px, calc(var(--tc-cut-br) - 0.414 * var(--tc-line)));
      --i-bl: max(0px, calc(var(--tc-cut-bl) - 0.414 * var(--tc-line)));
      clip-path: polygon(
        var(--tc-cut-tl) 0,
        calc(100% - var(--tc-cut-tr)) 0,
        100% var(--tc-cut-tr),
        100% calc(100% - var(--tc-cut-br)),
        calc(100% - var(--tc-cut-br)) 100%,
        var(--tc-cut-bl) 100%,
        0 calc(100% - var(--tc-cut-bl)),
        0 var(--tc-cut-tl),
        var(--tc-cut-tl) 0,
        calc(var(--tc-line) + var(--i-tl)) var(--tc-line),
        var(--tc-line) calc(var(--tc-line) + var(--i-tl)),
        var(--tc-line) calc(100% - var(--tc-line) - var(--i-bl)),
        calc(var(--tc-line) + var(--i-bl)) calc(100% - var(--tc-line)),
        calc(100% - var(--tc-line) - var(--i-br)) calc(100% - var(--tc-line)),
        calc(100% - var(--tc-line)) calc(100% - var(--tc-line) - var(--i-br)),
        calc(100% - var(--tc-line)) calc(var(--tc-line) + var(--i-tr)),
        calc(100% - var(--tc-line) - var(--i-tr)) var(--tc-line),
        calc(var(--tc-line) + var(--i-tl)) var(--tc-line)
      );
    }

    svg {
      flex: none;
      width: $section-text-font-size;
      height: $section-text-font-size;
      fill: $main-light;
      transition:
        fill 0.2s ease,
        filter 0.2s ease;
    }

    // Directional chevron instead of decorative ticks: these are links, so
    // the trailing mark should say "go", not repeat the etch vocabulary.
    &::after {
      content: "";
      flex: none;
      margin-left: auto;
      width: 0.9rem;
      height: 0.9rem;
      border-top: 0.15rem solid $main-darkish;
      border-right: 0.15rem solid $main-darkish;
      transform: rotate(45deg);
      transition: border-color 0.2s ease;
    }

    &:hover,
    &:focus-visible {
      // The cards' morph, chip-scaled: TL/BR snap to sharp 90°, TR/BL
      // deepen, the stroke thickens.
      --tc-cut-tl: 0rem;
      --tc-cut-tr: 1.6rem;
      --tc-cut-br: 0rem;
      --tc-cut-bl: 1.6rem;
      --tc-line: 0.3rem;
      color: $gold-light;
      background: rgba($gold, 0.08);
      text-shadow: $text-glow-faint;
      outline: none;

      &::before {
        background-color: $gold;
      }

      svg {
        fill: $gold-light;
        filter: drop-shadow(0 0 0.5rem $gold-semi-transparent);
      }

      &::after {
        border-color: $gold;
      }
    }
  }

  // Tablet: keep the two-column deck, trim the image share and type.
  @media all and (max-width: $desktop-medium) {
    gap: 2rem;

    .viewport {
      flex-basis: 46%;
    }

    h4 {
      font-size: $section-title-font-size-mobile-large;
    }

    .channel {
      padding: 1.1rem 1.5rem;
      font-size: $section-text-font-size-mobile-large;

      svg {
        width: $section-text-font-size-mobile-large;
        height: $section-text-font-size-mobile-large;
      }
    }
  }

  // Stacked below tablet width: the framed image spans full width at its
  // native ratio (media-fill never crops), channels become full-width tap
  // targets underneath.
  @media all and (max-width: $mobile-large) {
    flex-direction: column;
    align-items: stretch;
    gap: $tab-mobile-large;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: $tab-quadruple-mobile-large;

    .viewport {
      flex: none;
      width: 100%;
    }

    .channels {
      margin-top: $tab-mobile-large;
      gap: 1.2rem;
    }
  }

  @media all and (max-width: $mobile-small) {
    gap: $tab-mobile-small;
    margin-bottom: $tab-quadruple-mobile-small;

    h4 {
      font-size: $section-title-font-size-mobile-small;
    }

    .channels {
      margin-top: $tab-mobile-small;
      gap: 1rem;
    }

    .channel {
      gap: 1.2rem;
      padding: 1rem 1.2rem;
      font-size: $section-text-font-size-mobile-small;

      svg {
        width: $section-text-font-size-mobile-small;
        height: $section-text-font-size-mobile-small;
      }

      // Shorter plate → shallower deep cuts on hover so it doesn't
      // over-notch (technoCardMobile's rule, chip-scaled).
      &:hover,
      &:focus-visible {
        --tc-cut-tr: 1.2rem;
        --tc-cut-bl: 1.2rem;
      }
    }
  }
}
